import { generateKey } from 'services/generators'

const defaults = {
  children: [],
  hasChildren: false,
  isExpanded: false,
  isChildrenLoading: false,
  data: {
    title: 'Title'
  }
}

const slugKey = 'url'

const convertChildItem = ({
  key,
  child,
  isSecondDepth,
  currentSlug = null,
  childrenNames = []
}) => {
  const {
    title, text, links = [], ...rest
  } = child

  const linksFiltered = links.filter(link => 'text' in link)
  const childrenSlugs = linksFiltered.map(link => link.url)

  const isChildrenCurrentSlug = currentSlug
    ? childrenSlugs.find(childSlug => childSlug === currentSlug)
    : false

  const id = isSecondDepth ? text : title
  const slug = rest[slugKey]
  const isCurrent = currentSlug ? slug === currentSlug : false

  const childItem = {
    [key]: {
      id: key,
      ...defaults,
      children: childrenNames,
      hasChildren: links.length > 0,
      isExpanded: isChildrenCurrentSlug,
      isCurrent,
      data: {
        title: id,
        text: id,
        links,
        ...rest
      }
    }
  }
  return childItem;
}

// generates a new item
export const generateItem = ({ id, slug, title }) => {
  const key = slug || id
  const item = {
    [key]: {
      id: key,
      ...defaults,
      children: [],
      hasChildren: false,
      isExpanded: false,
      data: {
        id,
        title: title || id,
        text: title || id,
        url: typeof slug === 'string' ? slug : `page/${id}`
      }
    }
  }
  return item
}

// adds an object of new items to the tree
// returns the new tree
export const addItemsToTree = (schema, newItems) => {
  const { rootId, items } = schema
  const newChildren = Object.keys(newItems)
  const rootChildren = items[rootId].children

  const newRootItem = {
    ...items[rootId],
    children: [
      ...rootChildren,
      ...newChildren,
    ]
  }
  const newSchema = {
    rootId,
    items: {
      ...items,
      [rootId]: newRootItem,
      ...newItems
    }
  }
  return newSchema
}

// current slug is used to open the navigation if an item with that slug is found
export default function navToTree(data, currentSlug = null) {
  // tabs = children
  const { title: rootId = 'root', tabs: children = [], ...rest } = data
  const schema = {
    rootId,
    items: {
      [rootId]: {
        id: rootId,
        ...defaults,
        data: {
          title: rootId,
          ...rest
        }
      }
    }
  }

  let childItems = {}
  if (children.length > 0) {
    const { items } = schema
    // generating a unique key from the title
    // this way we can have multiple items with the same title
    const childrenWithUniqueKeys = children.map(child => ({
      original: child,
      key: generateKey(child.title)
    }))
    const childrenNames = childrenWithUniqueKeys.map(child => child.key)

    const rootItem = items[rootId]
    rootItem.children = childrenNames
    rootItem.hasChildren = true
    rootItem.isExpanded = true

    let childItemsArr = []

    // recursive power is over 8000
    // readability below -8000

    // this function basically goes through all tabs and its links
    // and generates a tree for the @atlaskit/tree format
    const createChildren = (children, isLink = false) => {
      children.forEach(({ original: child, key }) => {
        const linksFiltered = child.links && child.links.length > 0
          ? child.links.filter(({ text }) => (text !== null))
          : [];

        const linksWithUniqueKeys = linksFiltered.map(link => ({
          original: link,
          key: generateKey(link.text)
        }))

        const childrenNames = linksWithUniqueKeys.map(child => child.key)
        const childItem = convertChildItem({
          key,
          child,
          isSecondDepth: isLink,
          currentSlug,
          childrenNames
        })

        if (child.links && child.links.length > 0) createChildren(linksWithUniqueKeys, true)

        childItemsArr = [...childItemsArr, childItem]
      })
    }

    createChildren(childrenWithUniqueKeys)

    childItemsArr.forEach(child => {
      childItems = { ...childItems, ...child }
    })
  }

  schema.items = {
    ...schema.items,
    ...childItems,
  }


  // remove links present on initialProps (the editor for now doesnt support it)
  delete schema.items.links
  return schema
}
